/**
 * Link Preview Editor Component
 *
 * Allows Plus users to customize Open Graph metadata for link sharing.
 * Controls how links appear when shared on social media platforms.
 *
 * @module components/link-preview-editor
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Image,
  Type,
  FileText,
  ChevronDown,
  ChevronUp,
  Crown,
  Loader2,
  Check,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/client/lib/api';
import { useFeatures } from '@/client/hooks/use-features';

interface LinkPreview {
  title?: string;
  description?: string;
  image?: string;
}

interface LinkPreviewEditorProps {
  shortCode: string;
  linkTitle?: string;
  destinationUrl: string;
  currentPreview?: LinkPreview;
}

export function LinkPreviewEditor({
  shortCode,
  linkTitle,
  destinationUrl,
  currentPreview,
}: LinkPreviewEditorProps) {
  const { hasFeature } = useFeatures();
  const canCustomizePreview = hasFeature('custom_link_previews');
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Extract primitive values from currentPreview to avoid object dependency issues
  const currentTitle = currentPreview?.title || '';
  const currentDescription = currentPreview?.description || '';
  const currentImage = currentPreview?.image || '';

  // Form state
  const [title, setTitle] = useState(currentTitle);
  const [description, setDescription] = useState(currentDescription);
  const [image, setImage] = useState(currentImage);

  // Track if form has changes (using primitive dependencies)
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const hasTitle = title !== currentTitle;
    const hasDesc = description !== currentDescription;
    const hasImg = image !== currentImage;
    setHasChanges(hasTitle || hasDesc || hasImg);
  }, [title, description, image, currentTitle, currentDescription, currentImage]);

  // Reset form when currentPreview values change (using primitive dependencies)
  useEffect(() => {
    setTitle(currentTitle);
    setDescription(currentDescription);
    setImage(currentImage);
  }, [currentTitle, currentDescription, currentImage]);

  const updateMutation = useMutation({
    mutationFn: async (preview: LinkPreview) => {
      const response = await apiFetch(`/api/links/${shortCode}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meta: { preview },
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update preview');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', shortCode] });
      toast.success('Link preview updated!');
      setHasChanges(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      title: title || undefined,
      description: description || undefined,
      image: image || undefined,
    });
  };

  const handleReset = () => {
    setTitle(currentTitle);
    setDescription(currentDescription);
    setImage(currentImage);
  };

  // For display purposes
  const displayTitle = title || linkTitle || 'Armenian Link';
  const displayDescription =
    description || `Check out this link: ${new URL(destinationUrl).hostname}`;
  const displayImage = image || 'https://armenian.link/og-default.png';

  // Non-Pro users see upgrade prompt
  if (!canCustomizePreview) {
    return (
      <div className="rounded-2xl border bg-gradient-to-br from-purple-500/5 via-card to-indigo-500/5 p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
            <Image className="h-6 w-6 text-purple-500" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold flex items-center gap-2">
              Custom Link Previews
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-purple-500/10 text-purple-600">
                <Crown className="h-3 w-3" />
                Pro
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Control how your links appear when shared on Twitter, Facebook, LinkedIn, iMessage,
              and more.
            </p>
            <Link
              to="/settings/billing"
              className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-primary hover:underline"
            >
              <Crown className="h-4 w-4" />
              Upgrade to Pro
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border bg-card overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <Image className="h-5 w-5 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold">Link Preview</h3>
            <p className="text-sm text-muted-foreground">
              Customize how this link appears on social media
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 text-xs">
              Unsaved
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 animate-in fade-in slide-in-from-top-2">
          {/* Preview toggle */}
          <div className="flex justify-end">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showPreview
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              <Eye className="h-4 w-4" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>

          {/* Live Preview */}
          {showPreview && (
            <div className="rounded-xl border bg-muted/30 p-4">
              <p className="text-xs text-muted-foreground mb-3">Preview (Twitter/Facebook style)</p>
              <div className="rounded-lg border bg-card overflow-hidden max-w-md">
                {displayImage && (
                  <div className="aspect-[1.91/1] bg-muted relative">
                    <img
                      src={displayImage}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://armenian.link/og-default.png';
                      }}
                    />
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-muted-foreground mb-1">arml.ink</p>
                  <p className="font-semibold text-sm line-clamp-2">{displayTitle}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                    {displayDescription}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <Type className="h-4 w-4 text-muted-foreground" />
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={linkTitle || 'Enter a custom title…'}
                maxLength={120}
              />
              <p className="text-xs text-muted-foreground mt-1">{title.length}/120 characters</p>
            </div>

            {/* Description */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder="Enter a description for social sharing…"
                rows={3}
                maxLength={240}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {description.length}/240 characters
              </p>
            </div>

            {/* Image URL */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <Image className="h-4 w-4 text-muted-foreground" />
                Image URL
              </label>
              <input
                type="url"
                value={image}
                onChange={(e) => setImage(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="https://example.com/image.jpg"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Recommended: 1200x630 pixels for best display
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            {hasChanges && (
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border hover:bg-muted transition-colors text-sm"
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm font-medium"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Save Preview
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
